import { Icon } from "@iconify/react/dist/iconify.js";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { licenseQueryOptions } from "$/modules/projects/lib/queries";
import { AppearanceTab } from "$/modules/settings/ui/appearance-tab";
import { Button } from "$/modules/shared/ui/button";
import { ConstructionHeader } from "$/modules/shared/ui/construction-header";
import { PageContainer } from "$/modules/shared/ui/page-container";

export const Route = createFileRoute("/$locale/$userId/profile/settings/")({
	component: ProfileSettingsPage,
	loader: async ({ params, context: { queryClient } }) => {
		await queryClient.ensureQueryData({
			...licenseQueryOptions(params.userId),
			revalidateIfStale: true,
		});
	},
});

function ProfileSettingsPage() {
	const t = useTranslations();
	const { locale, userId } = Route.useParams();
	const { data: license } = useSuspenseQuery(licenseQueryOptions(userId));

	return (
		<PageContainer>
			<ConstructionHeader
				titleKey="profile.settingsTitle"
				description={{
					hasKey: true,
					descriptionKey: "profile.settingsDescription",
				}}
			/>
			<div className="mb-6 flex flex-wrap gap-2">
				<Button asChild variant="muted">
					<Link to="/$locale/$userId/profile/me" params={{ locale, userId }}>
						{t("profile.navigation.profile")}
					</Link>
				</Button>
				<Button asChild variant="default">
					<Link
						to="/$locale/$userId/profile/settings"
						params={{ locale, userId }}
					>
						{t("profile.navigation.settings")}
					</Link>
				</Button>
			</div>

			{license && (
				<section className="mb-6 rounded-xl border border-default-200 bg-content1 p-5">
					<div className="flex items-start gap-3">
						<span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
							<Icon icon="lucide:badge-check" className="size-5" />
						</span>
						<div>
							<h2 className="font-semibold text-lg">
								{t("profile.license.title")}
							</h2>
							<p className="mt-1 text-foreground-600">
								{license.plan === "studio"
									? t("profile.license.studio")
									: t("profile.license.free", {
											count: license.ownedProjectCount,
											limit: license.projectLimit ?? 3,
										})}
							</p>
							{license.plan === "free" && !license.canCreateProject && (
								<p className="mt-2 text-sm text-warning-600">
									{t("profile.license.limitReached")}
								</p>
							)}
						</div>
					</div>
				</section>
			)}

			<AppearanceTab />

			<div className="mt-12">
				<Button asChild variant="muted">
					<Link to="/$locale/$userId/projects" params={{ locale, userId }}>
						{<Icon icon="lucide:arrow-left" className="size-5" />}
						{t("common.backToDashboard")}
					</Link>
				</Button>
			</div>
		</PageContainer>
	);
}
